import axios from 'axios'

// Ensure axios has authorization header
axios.defaults.headers.common.Authorization = `Bearer ${localStorage.getItem('token')}`

const API_BASE = process.env.NODE_ENV !== 'production' ? 'https://tredbackend.test/api' : 'https://mytredspace.ru/api'

class ParentalControlAPI {
  /**
   * Admin endpoints - Parent Management
   */

  // Get all parent accounts
  async getParents() {
    return axios.get(`${API_BASE}/admin/parental-control/parents`)
  }

  // Create a new parent account
  async createParent(parentData) {
    return axios.post(`${API_BASE}/admin/parental-control/parents`, parentData)
  }

  // Get students available for binding
  async getStudentsForBinding() {
    return axios.get(`${API_BASE}/admin/parental-control/students-for-binding`)
  }

  // Bind student to parent
  async bindStudent(bindingData) {
    return axios.post(`${API_BASE}/admin/parental-control/bind-student`, bindingData)
  }

  // Get all parent-student relationships
  async getRelationships() {
    return axios.get(`${API_BASE}/admin/parental-control/relationships`)
  }

  // Remove parent-student relationship
  async deleteRelationship(relationshipId) {
    return axios.delete(`${API_BASE}/admin/parental-control/relationships/${relationshipId}`)
  }

  /**
   * Parent Dashboard endpoints
   */

  // Get parent dashboard overview
  async getParentDashboard() {
    return axios.get(`${API_BASE}/parental-control/dashboard`)
  }

  // Get detailed student progress
  async getStudentProgress(studentId) {
    return axios.get(`${API_BASE}/parental-control/student/${studentId}/progress`)
  }

  /**
   * Helper methods
   */

  // Validate parent data before sending
  validateParentData(data) {
    const errors = {}

    if (!data.name || data.name.trim().length < 2) {
      errors.name = 'Имя должно содержать минимум 2 символа'
    }

    if (!data.email || !this.isValidEmail(data.email)) {
      errors.email = 'Введите корректный email адрес'
    }

    if (!data.password || data.password.length < 6) {
      errors.password = 'Пароль должен содержать минимум 6 символов'
    }

    if (data.phone && !this.isValidPhone(data.phone)) {
      errors.phone = 'Неверный формат номера телефона'
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }

  // Validate binding data
  validateBindingData(data) {
    const errors = {}

    if (!data.parent_id) {
      errors.parent_id = 'Выберите родителя'
    }

    if (!data.student_id) {
      errors.student_id = 'Выберите студента'
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }

  // Email validation
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Phone validation (basic)
  isValidPhone(phone) {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
    return phoneRegex.test(phone.replace(/\s/g, ''))
  }

  /**
   * Error handling helpers
   */

  // Parse API error response
  parseErrorResponse(error) {
    if (error.response) {
      // Server responded with error status
      const { data, status } = error.response

      switch (status) {
        case 400:
          return {
            message: data.error || 'Неверные данные запроса',
            errors: data.errors || {}
          }
        case 401:
          return {
            message: 'Необходима авторизация',
            errors: {}
          }
        case 403:
          return {
            message: 'Доступ запрещен',
            errors: {}
          }
        case 404:
          return {
            message: 'Ресурс не найден',
            errors: {}
          }
        case 422:
          return {
            message: 'Ошибка валидации данных',
            errors: data.errors || {}
          }
        case 500:
          return {
            message: 'Внутренняя ошибка сервера',
            errors: {}
          }
        default:
          return {
            message: data.error || 'Произошла ошибка',
            errors: data.errors || {}
          }
      }
    } else if (error.request) {
      // Network error
      return {
        message: 'Ошибка соединения с сервером',
        errors: {}
      }
    } else {
      // Other error
      return {
        message: 'Произошла неизвестная ошибка',
        errors: {}
      }
    }
  }

  /**
   * Data formatting helpers
   */

  // Format parent data for display
  formatParentData(parent) {
    return {
      ...parent,
      displayName: parent.name,
      contactInfo: [parent.email, parent.phone].filter(Boolean).join(' • '),
      studentsCount: parent.students_count || 0,
      hasAccount: !!parent.account,
      balance: parent.account?.balance || 0
    }
  }

  // Format student data for display
  formatStudentData(student) {
    return {
      ...student,
      displayName: student.name,
      contactInfo: [student.email].filter(Boolean).join(' • '),
      parentsCount: student.parents_count || 0,
      hasParents: student.has_parents || false,
      balance: student.balance || 0
    }
  }

  // Format relationship data for display
  formatRelationshipData(relationship) {
    return {
      ...relationship,
      parentName: relationship.parent?.name || 'Неизвестно',
      studentName: relationship.student?.name || 'Неизвестно',
      createdBy: relationship.created_by?.name || 'Система',
      isActive: relationship.is_active,
      statusText: relationship.is_active ? 'Активна' : 'Неактивна',
      statusVariant: relationship.is_active ? 'success' : 'warning',
      createdDate: new Date(relationship.created_at).toLocaleDateString('ru-RU')
    }
  }

  /**
   * Batch operations
   */

  // Get all data needed for admin interface
  async getAllAdminData() {
    try {
      const [parentsResponse, relationshipsResponse, studentsResponse] = await Promise.all([
        this.getParents(),
        this.getRelationships(),
        this.getStudentsForBinding()
      ])

      return {
        success: true,
        data: {
          parents: parentsResponse.data.map(this.formatParentData),
          relationships: relationshipsResponse.data.map(this.formatRelationshipData),
          students: studentsResponse.data.map(this.formatStudentData)
        }
      }
    } catch (error) {
      return {
        success: false,
        error: this.parseErrorResponse(error)
      }
    }
  }

  // Create parent and optionally bind students
  async createParentWithStudents(parentData, studentIds = []) {
    try {
      // Create parent
      const parentResponse = await this.createParent(parentData)
      const newParent = parentResponse.data.parent

      // Bind students if provided
      const bindings = []
      for (const studentId of studentIds) {
        try {
          const bindingResponse = await this.bindStudent({
            parent_id: newParent.id,
            student_id: studentId,
            notes: `Автоматически создано при создании родителя`
          })
          bindings.push(bindingResponse.data.relationship)
        } catch (bindingError) {
          console.warn(`Failed to bind student ${studentId}:`, bindingError)
        }
      }

      return {
        success: true,
        data: {
          parent: newParent,
          bindings
        }
      }
    } catch (error) {
      return {
        success: false,
        error: this.parseErrorResponse(error)
      }
    }
  }
}

// Create singleton instance
const parentalControlAPI = new ParentalControlAPI()

// Export both class and instance for flexibility
export default parentalControlAPI
export { ParentalControlAPI }